import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { FileText, Printer, Filter, Tag, TrendingUp, TrendingDown, DollarSign, Calendar, Download, BarChart3, PieChart as PieChartIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Analytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [income, setIncome] = useState([]);
  const [categories, setCategories] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const printRef = useRef(null);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('monthly'); // 'monthly', 'charts' or 'reports'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Report Filters
  const [reportType, setReportType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [businessOnly, setBusinessOnly] = useState(false); // Filter for business expenses
  const [reportData, setReportData] = useState(null);

  // Free users get full access for first 20 receipts
  const isPro = user?.role === 'admin' || user?.subscription_status === 'active';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [transactionsRes, receiptsRes, incomeRes, expenseCategoriesRes, incomeCategoriesRes] = await Promise.all([
        api.get('/transactions'),
        api.get('/receipts'),
        api.get('/income'),
        api.get('/categories/expense'),
        api.get('/categories/income'),
      ]);

      setTransactions(transactionsRes.data);
      setReceipts(receiptsRes.data);
      setIncome(incomeRes.data);
      
      const allCategories = [
        ...expenseCategoriesRes.data.map(c => ({ ...c, categoryType: 'expense' })),
        ...incomeCategoriesRes.data.map(c => ({ ...c, categoryType: 'income' })),
      ];
      setCategories(allCategories);
      
      // Normalize merchant names to avoid duplicates (case-insensitive)
      const merchantMap = new Map();
      transactionsRes.data
        .filter(t => t.type === 'expense' && t.merchant)
        .forEach(t => {
          const normalized = t.merchant.trim().toLowerCase();
          // Keep the first occurrence of each normalized name (usually proper case)
          if (!merchantMap.has(normalized)) {
            merchantMap.set(normalized, t.merchant);
          }
        });
      const uniqueMerchants = [...merchantMap.values()].sort((a, b) => 
        a.toLowerCase().localeCompare(b.toLowerCase())
      );
      setMerchants(uniqueMerchants);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate category breakdown for charts
  const expensesByCategory = receipts.reduce((acc, receipt) => {
    acc[receipt.category] = (acc[receipt.category] || 0) + receipt.amount;
    return acc;
  }, {});

  const categoryData = Object.entries(expensesByCategory).map(([name, value]) => ({
    name,
    value,
  }));

  const expensesByMerchant = receipts.reduce((acc, receipt) => {
    acc[receipt.merchant] = (acc[receipt.merchant] || 0) + receipt.amount;
    return acc;
  }, {});

  const merchantData = Object.entries(expensesByMerchant)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const COLORS = ['#6366f1', '#10b981', '#06b6d4', '#f43f5e', '#f59e0b'];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="backdrop-blur-xl bg-black/80 border border-white/10 rounded-lg p-3 text-white shadow-xl">
          <p className="font-medium">{payload[0].name}</p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  // Report functions
  const generateReport = () => {
    let filtered = [...transactions];
    
    if (reportType === 'expenses') {
      filtered = filtered.filter(t => t.type === 'expense');
    } else if (reportType === 'income') {
      filtered = filtered.filter(t => t.type === 'income');
    } else if (reportType === 'business') {
      // Business expenses only
      filtered = filtered.filter(t => t.type === 'expense' && t.business_expense === true);
    }
    
    // Apply business filter even for other report types if checked
    if (businessOnly && reportType !== 'business') {
      filtered = filtered.filter(t => t.business_expense === true);
    }
    
    if (selectedCategory) {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }
    
    if (selectedMerchant) {
      // Case-insensitive merchant matching
      const normalizedSelected = selectedMerchant.toLowerCase();
      filtered = filtered.filter(t => t.merchant && t.merchant.toLowerCase() === normalizedSelected);
    }
    
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filtered = filtered.filter(t => parseDate(t.date) >= fromDate);
    }
    
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59);
      filtered = filtered.filter(t => parseDate(t.date) <= toDate);
    }
    
    const categoryTotals = {};
    let totalExpenses = 0;
    let totalIncome = 0;
    let totalBusinessExpenses = 0;
    
    filtered.forEach(t => {
      const cat = t.category || 'Uncategorized';
      if (!categoryTotals[cat]) {
        categoryTotals[cat] = { expense: 0, income: 0, count: 0, businessExpense: 0 };
      }
      
      if (t.type === 'expense') {
        categoryTotals[cat].expense += t.amount;
        totalExpenses += t.amount;
        if (t.business_expense) {
          categoryTotals[cat].businessExpense += t.amount;
          totalBusinessExpenses += t.amount;
        }
      } else {
        categoryTotals[cat].income += t.amount;
        totalIncome += t.amount;
      }
      categoryTotals[cat].count++;
    });
    
    filtered.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    
    setReportData({
      transactions: filtered,
      categoryTotals,
      totalExpenses,
      totalIncome,
      totalBusinessExpenses,
      netBalance: totalIncome - totalExpenses,
      filters: { reportType, category: selectedCategory, merchant: selectedMerchant, dateFrom, dateTo, businessOnly }
    });
  };

  const parseDate = (dateStr) => {
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      return new Date(parts[2], parts[0] - 1, parts[1]);
    }
    return new Date(dateStr);
  };

  const formatDate = (dateStr) => parseDate(dateStr).toLocaleDateString('en-US');
  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  // Calculate monthly summary for the selected year
  const monthlySummary = useMemo(() => {
    const months = {};
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Initialize all months
    monthNames.forEach((name, index) => {
      months[index] = { name, income: 0, expenses: 0, net: 0, transactions: 0 };
    });

    transactions.forEach(t => {
      const dateStr = t.date || '';
      let month, year;
      
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        month = parseInt(parts[0]) - 1;
        year = parseInt(parts[2]);
      } else if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        year = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
      } else {
        return;
      }

      if (year !== selectedYear || month < 0 || month > 11) return;

      const amount = parseFloat(t.amount) || 0;
      if (t.type === 'income') {
        months[month].income += amount;
      } else {
        months[month].expenses += amount;
      }
      months[month].transactions++;
    });

    // Calculate net
    Object.values(months).forEach(m => {
      m.net = m.income - m.expenses;
    });

    return months;
  }, [transactions, selectedYear]);

  // Calculate totals for selected year
  const yearTotals = useMemo(() => {
    let income = 0, expenses = 0;
    Object.values(monthlySummary).forEach(m => {
      income += m.income;
      expenses += m.expenses;
    });
    return { income, expenses, net: income - expenses };
  }, [monthlySummary]);

  // Calculate category breakdown for selected year
  const yearCategoryBreakdown = useMemo(() => {
    const categories = {};
    transactions.forEach(t => {
      const dateStr = t.date || '';
      let year;
      if (dateStr.includes('/')) {
        year = parseInt(dateStr.split('/')[2]);
      } else if (dateStr.includes('-')) {
        year = parseInt(dateStr.split('-')[0]);
      }
      
      if (year !== selectedYear) return;
      if (t.type !== 'expense') return;

      const cat = t.category || 'Other';
      categories[cat] = (categories[cat] || 0) + (parseFloat(t.amount) || 0);
    });
    return Object.entries(categories).sort((a, b) => b[1] - a[1]);
  }, [transactions, selectedYear]);

  // Get unique years from transactions
  const availableYears = useMemo(() => {
    const years = new Set();
    transactions.forEach(t => {
      const dateStr = t.date || '';
      let year;
      if (dateStr.includes('/')) {
        year = parseInt(dateStr.split('/')[2]);
      } else if (dateStr.includes('-')) {
        year = parseInt(dateStr.split('-')[0]);
      }
      if (year && year > 2000 && year < 2100) years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [transactions]);

  // Colors for charts
  const chartColors = [
    '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', 
    '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'
  ];

  const handleExportMonthlyReport = () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    
    let report = `FINSCAN USA - MONTHLY FINANCIAL REPORT ${selectedYear}\n`;
    report += `${'='.repeat(60)}\n\n`;
    
    report += `ANNUAL SUMMARY\n`;
    report += `${'-'.repeat(40)}\n`;
    report += `Total Income:     ${formatCurrency(yearTotals.income)}\n`;
    report += `Total Expenses:   ${formatCurrency(yearTotals.expenses)}\n`;
    report += `Net Profit/Loss:  ${formatCurrency(yearTotals.net)}\n`;
    report += `Profit Margin:    ${yearTotals.income > 0 ? ((yearTotals.net / yearTotals.income) * 100).toFixed(1) : 0}%\n\n`;
    
    report += `MONTHLY BREAKDOWN\n`;
    report += `${'-'.repeat(60)}\n`;
    report += `Month          Income        Expenses      Net\n`;
    report += `${'-'.repeat(60)}\n`;
    
    Object.values(monthlySummary).forEach((m, i) => {
      const monthName = monthNames[i].padEnd(14);
      const income = formatCurrency(m.income).padStart(12);
      const expenses = formatCurrency(m.expenses).padStart(12);
      const net = formatCurrency(m.net).padStart(12);
      report += `${monthName}${income}  ${expenses}  ${net}\n`;
    });
    
    report += `${'-'.repeat(60)}\n`;
    report += `TOTAL         ${formatCurrency(yearTotals.income).padStart(12)}  ${formatCurrency(yearTotals.expenses).padStart(12)}  ${formatCurrency(yearTotals.net).padStart(12)}\n\n`;
    
    report += `EXPENSES BY CATEGORY\n`;
    report += `${'-'.repeat(40)}\n`;
    yearCategoryBreakdown.forEach(([cat, amount]) => {
      const pct = yearTotals.expenses > 0 ? ((amount / yearTotals.expenses) * 100).toFixed(1) : 0;
      report += `${cat.padEnd(20)} ${formatCurrency(amount).padStart(12)}  (${pct}%)\n`;
    });
    
    report += `\n\nGenerated by FinScan USA on ${new Date().toLocaleDateString()}\n`;
    
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monthly_report_${selectedYear}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Monthly report downloaded!');
  };

  const handlePrint = () => {
    if (!reportData) return;
    
    const reportTitle = getReportTitle();
    const dateRange = reportData.filters.dateFrom && reportData.filters.dateTo 
      ? `${formatDate(reportData.filters.dateFrom)} - ${formatDate(reportData.filters.dateTo)}`
      : 'All dates';
    
    // Build HTML content
    let categoryRows = Object.entries(reportData.categoryTotals)
      .sort((a, b) => (b[1].expense + b[1].income) - (a[1].expense + a[1].income))
      .map(([category, totals]) => `
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${category}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: center;">${totals.count}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right; color: ${totals.income > 0 ? '#059669' : '#6b7280'};">${totals.income > 0 ? '+' + formatCurrency(totals.income) : '-'}</td>
          <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right; color: ${totals.expense > 0 ? '#dc2626' : '#6b7280'};">${totals.expense > 0 ? '-' + formatCurrency(totals.expense) : '-'}</td>
        </tr>
      `).join('');
    
    let transactionRows = reportData.transactions.map(t => `
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${formatDate(t.date)}</td>
        <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${t.merchant || t.description || '-'}</td>
        <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${t.category}</td>
        <td style="padding: 8px; border-bottom: 1px solid #e5e7eb; text-align: right; color: ${t.type === 'expense' ? '#dc2626' : '#059669'};">
          ${t.type === 'expense' ? '-' : '+'}${formatCurrency(t.amount)}
        </td>
      </tr>
    `).join('');
    
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>FinScan USA - ${reportTitle}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; padding: 20px; color: #1a1a1a; font-size: 12px; }
    .header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #333; }
    .header h1 { font-size: 20px; margin-bottom: 5px; }
    .header p { color: #666; }
    .summary { display: flex; justify-content: space-around; margin-bottom: 20px; }
    .summary-item { text-align: center; padding: 10px 20px; border: 1px solid #ddd; border-radius: 8px; }
    .summary-item h3 { font-size: 10px; color: #666; margin-bottom: 5px; }
    .summary-item p { font-size: 16px; font-weight: bold; }
    .income { color: #059669; }
    .expense { color: #dc2626; }
    .balance { color: #4f46e5; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th { background: #f3f4f6; padding: 10px 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #e5e7eb; font-size: 11px; }
    .footer { text-align: center; margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; color: #666; font-size: 10px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>${reportTitle}</h1>
    <p>${dateRange}</p>
  </div>
  
  <div class="summary">
    <div class="summary-item">
      <h3>INCOME</h3>
      <p class="income">${formatCurrency(reportData.totalIncome)}</p>
    </div>
    <div class="summary-item">
      <h3>EXPENSES</h3>
      <p class="expense">${formatCurrency(reportData.totalExpenses)}</p>
    </div>
    <div class="summary-item">
      <h3>BALANCE</h3>
      <p class="${reportData.netBalance >= 0 ? 'balance' : 'expense'}">${formatCurrency(reportData.netBalance)}</p>
    </div>
  </div>
  
  <h2 style="font-size: 14px; margin-bottom: 10px;">Category Summary</h2>
  <table>
    <thead>
      <tr>
        <th>Category</th>
        <th style="text-align: center;">Entries</th>
        <th style="text-align: right;">Income</th>
        <th style="text-align: right;">Expenses</th>
      </tr>
    </thead>
    <tbody>${categoryRows}</tbody>
  </table>
  
  <h2 style="font-size: 14px; margin-bottom: 10px;">Transaction Details (${reportData.transactions.length} entries)</h2>
  <table>
    <thead>
      <tr>
        <th>Date</th>
        <th>Description</th>
        <th>Category</th>
        <th style="text-align: right;">Amount</th>
      </tr>
    </thead>
    <tbody>${transactionRows}</tbody>
  </table>
  
  <div class="footer">
    <p>FinScan USA • Report generated ${new Date().toLocaleString('en-US')}</p>
  </div>
</body>
</html>`;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    // For mobile - create downloadable file
    const link = document.createElement('a');
    link.href = url;
    link.download = `finscan-report-${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Report downloaded! Open the file to print.');
  };

  const getReportTitle = () => {
    let title = 'Financial Report';
    if (reportType === 'expenses') title = 'Expense Report';
    else if (reportType === 'income') title = 'Income Report';
    else if (reportType === 'business') title = 'Business Expense Report';
    if (businessOnly && reportType !== 'business') title += ' (Business Only)';
    if (selectedCategory) title += ` • ${selectedCategory}`;
    if (selectedMerchant) title += ` • ${selectedMerchant}`;
    return title;
  };

  const clearFilters = () => {
    setReportType('all');
    setSelectedCategory('');
    setSelectedMerchant('');
    setDateFrom('');
    setDateTo('');
    setBusinessOnly(false);
    setReportData(null);
  };

  const filteredCategories = categories.filter(c => {
    if (reportType === 'expenses' || reportType === 'business') return c.categoryType === 'expense';
    if (reportType === 'income') return c.categoryType === 'income';
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Free users get full access for first 20 receipts - no pro-only gate needed
  // The limit is enforced at receipt scanning, not feature access

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-2">
            Analytics
          </h1>
          <p className="text-lg text-muted-foreground">
            Detailed insights and printable reports for your finances
          </p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-8 p-1 bg-white/5 rounded-xl">
          <button
            onClick={() => setActiveTab('monthly')}
            data-testid="tab-monthly"
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'monthly' ? 'bg-white text-black' : 'text-muted-foreground hover:text-white'
            }`}
          >
            Monthly Summary
          </button>
          <button
            onClick={() => setActiveTab('charts')}
            data-testid="tab-charts"
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'charts' ? 'bg-white text-black' : 'text-muted-foreground hover:text-white'
            }`}
          >
            Charts
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            data-testid="tab-reports"
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'reports' ? 'bg-white text-black' : 'text-muted-foreground hover:text-white'
            }`}
          >
            Printable Reports
          </button>
        </div>

        {/* Monthly Summary Tab */}
        {activeTab === 'monthly' && (
          <div className="space-y-6">
            {/* Year Selector & Export */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-6 bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-pink-500/10 border border-indigo-500/20 rounded-2xl">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-indigo-400" />
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                  data-testid="year-selector"
                >
                  {availableYears.length > 0 ? (
                    availableYears.map(year => (
                      <option key={year} value={year} className="bg-[#0f172a]">{year}</option>
                    ))
                  ) : (
                    <option value={new Date().getFullYear()} className="bg-[#0f172a]">{new Date().getFullYear()}</option>
                  )}
                </select>
              </div>
              <button
                onClick={handleExportMonthlyReport}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                data-testid="export-monthly-report"
              >
                <Download className="w-4 h-4" />
                Export Report
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-gray-400">TOTAL INCOME</span>
                </div>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(yearTotals.income)}</p>
              </div>
              
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-red-400" />
                  <span className="text-sm text-gray-400">TOTAL EXPENSES</span>
                </div>
                <p className="text-2xl font-bold text-red-400">{formatCurrency(yearTotals.expenses)}</p>
              </div>
              
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-blue-400" />
                  <span className="text-sm text-gray-400">NET PROFIT</span>
                </div>
                <p className={`text-2xl font-bold ${yearTotals.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(yearTotals.net)}
                </p>
              </div>
              
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <PieChartIcon className="w-5 h-5 text-purple-400" />
                  <span className="text-sm text-gray-400">PROFIT MARGIN</span>
                </div>
                <p className={`text-2xl font-bold ${yearTotals.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {yearTotals.income > 0 ? ((yearTotals.net / yearTotals.income) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Monthly Bar Chart */}
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  INCOME VS EXPENSES BY MONTH
                </h3>
                <div className="h-48 flex items-end justify-between gap-1">
                  {Object.values(monthlySummary).map((m, i) => {
                    const maxValue = Math.max(
                      ...Object.values(monthlySummary).map(x => Math.max(x.income, x.expenses))
                    ) || 1;
                    const incomeHeight = (m.income / maxValue) * 100;
                    const expenseHeight = (m.expenses / maxValue) * 100;
                    
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full flex gap-0.5 h-36 items-end">
                          <div 
                            className="flex-1 bg-green-500 rounded-t-sm transition-all hover:bg-green-400"
                            style={{ height: `${incomeHeight}%` }}
                            title={`Income: ${formatCurrency(m.income)}`}
                          />
                          <div 
                            className="flex-1 bg-red-500 rounded-t-sm transition-all hover:bg-red-400"
                            style={{ height: `${expenseHeight}%` }}
                            title={`Expenses: ${formatCurrency(m.expenses)}`}
                          />
                        </div>
                        <span className="text-[10px] text-gray-500">{m.name}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-center gap-4 mt-3">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-500 rounded-sm" />
                    <span className="text-xs text-gray-400">Income</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-red-500 rounded-sm" />
                    <span className="text-xs text-gray-400">Expenses</span>
                  </div>
                </div>
              </div>

              {/* Expenses by Category */}
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2">
                  <PieChartIcon className="w-4 h-4" />
                  EXPENSES BY CATEGORY
                </h3>
                {yearCategoryBreakdown.length > 0 ? (
                  <div className="space-y-2">
                    {yearCategoryBreakdown.slice(0, 6).map(([cat, amount], i) => {
                      const pct = yearTotals.expenses > 0 ? (amount / yearTotals.expenses) * 100 : 0;
                      return (
                        <div key={cat}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-300">{cat}</span>
                            <span className="text-gray-400">{formatCurrency(amount)} ({pct.toFixed(0)}%)</span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all"
                              style={{ 
                                width: `${pct}%`,
                                backgroundColor: chartColors[i % chartColors.length]
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No expense data for {selectedYear}</p>
                )}
              </div>
            </div>

            {/* Monthly Table */}
            <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-x-auto">
              <h3 className="text-sm font-semibold text-gray-400 mb-4">MONTHLY BREAKDOWN</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-white/10">
                    <th className="pb-2 font-medium">Month</th>
                    <th className="pb-2 font-medium text-right">Income</th>
                    <th className="pb-2 font-medium text-right">Expenses</th>
                    <th className="pb-2 font-medium text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(monthlySummary).map((m, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 text-gray-300">{m.name}</td>
                      <td className="py-2 text-right text-green-400 font-mono">{formatCurrency(m.income)}</td>
                      <td className="py-2 text-right text-red-400 font-mono">{formatCurrency(m.expenses)}</td>
                      <td className={`py-2 text-right font-mono font-semibold ${m.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(m.net)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold border-t-2 border-white/20">
                    <td className="pt-3 text-white">TOTAL</td>
                    <td className="pt-3 text-right text-green-400 font-mono">{formatCurrency(yearTotals.income)}</td>
                    <td className="pt-3 text-right text-red-400 font-mono">{formatCurrency(yearTotals.expenses)}</td>
                    <td className={`pt-3 text-right font-mono ${yearTotals.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(yearTotals.net)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Charts Tab */}
        {activeTab === 'charts' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl">
                <h2 className="text-xl font-semibold mb-4">Expenses by Category</h2>
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-muted-foreground py-12">No data yet</p>
                )}
              </div>

              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl">
                <h2 className="text-xl font-semibold mb-4">Top 5 Merchants</h2>
                {merchantData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={merchantData} layout="vertical">
                      <XAxis type="number" stroke="rgba(255,255,255,0.1)" />
                      <YAxis dataKey="name" type="category" stroke="rgba(255,255,255,0.5)" width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill="#6366f1" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-muted-foreground py-12">No data yet</p>
                )}
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">Total Receipts</p>
                <p className="text-2xl font-mono font-bold">{receipts.length}</p>
              </div>
              <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">Income Entries</p>
                <p className="text-2xl font-mono font-bold">{income.length}</p>
              </div>
              <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">Categories</p>
                <p className="text-2xl font-mono font-bold">{categoryData.length}</p>
              </div>
              <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">Avg. Receipt</p>
                <p className="text-2xl font-mono font-bold">
                  {formatCurrency(receipts.length > 0 ? receipts.reduce((s, r) => s + r.amount, 0) / receipts.length : 0)}
                </p>
              </div>
            </div>
          </>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <>
            {/* Filters */}
            <div className="p-6 bg-white/5 border border-white/10 rounded-2xl mb-6">
              <div className="flex items-center space-x-2 mb-4">
                <Filter className="w-5 h-5 text-indigo-400" />
                <h2 className="text-lg font-semibold">Report Filters</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Report Type</label>
                  <select
                    value={reportType}
                    onChange={(e) => { setReportType(e.target.value); setSelectedCategory(''); setSelectedMerchant(''); }}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="report-type-select"
                  >
                    <option value="all" className="bg-[#0f172a]">All Transactions</option>
                    <option value="expenses" className="bg-[#0f172a]">Expenses Only</option>
                    <option value="income" className="bg-[#0f172a]">Income Only</option>
                    <option value="business" className="bg-[#0f172a]">Business Expenses</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="" className="bg-[#0f172a]">All Categories</option>
                    {filteredCategories.map((cat) => (
                      <option key={cat.id} value={cat.name} className="bg-[#0f172a]">{cat.name}</option>
                    ))}
                  </select>
                </div>

                {reportType !== 'income' && (
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">Merchant</label>
                    <select
                      value={selectedMerchant}
                      onChange={(e) => setSelectedMerchant(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      data-testid="merchant-select"
                    >
                      <option value="" className="bg-[#0f172a]">All Merchants</option>
                      {merchants.map((m) => (
                        <option key={m} value={m} className="bg-[#0f172a]">{m}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Date From</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Date To</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Business expense filter checkbox */}
              {reportType !== 'business' && reportType !== 'income' && (
                <div className="mt-4 flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="businessOnly"
                    checked={businessOnly}
                    onChange={(e) => setBusinessOnly(e.target.checked)}
                    data-testid="business-only-checkbox"
                    className="w-5 h-5 rounded border-white/10 bg-white/5 text-indigo-500 focus:ring-2 focus:ring-indigo-500"
                  />
                  <label htmlFor="businessOnly" className="text-sm text-muted-foreground">
                    Show only business expenses (for tax deductions)
                  </label>
                </div>
              )}

              <div className="flex flex-wrap gap-3 mt-6">
                <button
                  onClick={generateReport}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-full font-medium transition-colors flex items-center space-x-2"
                >
                  <FileText className="w-5 h-5" />
                  <span>Generate Report</span>
                </button>
                
                {reportData && (
                  <button
                    onClick={handlePrint}
                    className="bg-white text-black hover:bg-white/90 px-6 py-3 rounded-full font-medium transition-colors flex items-center space-x-2"
                  >
                    <Printer className="w-5 h-5" />
                    <span>Download for Print</span>
                  </button>
                )}
                
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 rounded-full font-medium border border-white/10 hover:bg-white/5 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Report Results */}
            {reportData && (
              <div ref={printRef} className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold mb-1">{getReportTitle()}</h2>
                  <p className="text-muted-foreground text-sm">
                    {reportData.filters.dateFrom && reportData.filters.dateTo 
                      ? `${formatDate(reportData.filters.dateFrom)} - ${formatDate(reportData.filters.dateTo)}`
                      : 'All dates'}
                  </p>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-6 bg-gradient-to-br from-green-500/20 to-transparent border border-green-500/30 rounded-2xl text-center">
                    <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-sm text-green-400 mb-1">Income</p>
                    <p className="text-2xl font-mono font-bold text-green-400">{formatCurrency(reportData.totalIncome)}</p>
                  </div>
                  
                  <div className="p-6 bg-gradient-to-br from-red-500/20 to-transparent border border-red-500/30 rounded-2xl text-center">
                    <TrendingDown className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="text-sm text-red-400 mb-1">Expenses</p>
                    <p className="text-2xl font-mono font-bold text-red-400">{formatCurrency(reportData.totalExpenses)}</p>
                  </div>
                  
                  <div className={`p-6 rounded-2xl text-center ${
                    reportData.netBalance >= 0 
                      ? 'bg-gradient-to-br from-indigo-500/20 to-transparent border border-indigo-500/30'
                      : 'bg-gradient-to-br from-orange-500/20 to-transparent border border-orange-500/30'
                  }`}>
                    <DollarSign className={`w-6 h-6 mx-auto mb-2 ${reportData.netBalance >= 0 ? 'text-indigo-400' : 'text-orange-400'}`} />
                    <p className={`text-sm mb-1 ${reportData.netBalance >= 0 ? 'text-indigo-400' : 'text-orange-400'}`}>Balance</p>
                    <p className={`text-2xl font-mono font-bold ${reportData.netBalance >= 0 ? 'text-indigo-400' : 'text-orange-400'}`}>
                      {formatCurrency(reportData.netBalance)}
                    </p>
                  </div>
                </div>

                {/* Category Summary */}
                <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                  <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                    <Tag className="w-5 h-5 text-indigo-400" />
                    <span>By Category</span>
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(reportData.categoryTotals)
                      .sort((a, b) => (b[1].expense + b[1].income) - (a[1].expense + a[1].income))
                      .map(([category, totals]) => (
                        <div key={category} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                          <div className="flex items-center space-x-3">
                            <span className="font-medium">{category}</span>
                            <span className="text-xs text-muted-foreground">({totals.count})</span>
                          </div>
                          <div className="flex items-center space-x-4">
                            {totals.income > 0 && <span className="text-green-400 font-mono">+{formatCurrency(totals.income)}</span>}
                            {totals.expense > 0 && <span className="text-red-400 font-mono">-{formatCurrency(totals.expense)}</span>}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Transactions List */}
                <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                  <h3 className="text-lg font-semibold mb-4">Transaction List ({reportData.transactions.length})</h3>
                  {reportData.transactions.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Date</th>
                            <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Description</th>
                            <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Category</th>
                            <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.transactions.slice(0, 50).map((t) => (
                            <tr key={t.id} className="border-b border-white/5 hover:bg-white/5">
                              <td className="py-3 px-2 text-sm">{formatDate(t.date)}</td>
                              <td className="py-3 px-2 font-medium">{t.merchant || t.description || '-'}</td>
                              <td className="py-3 px-2 text-sm text-muted-foreground">{t.category}</td>
                              <td className={`py-3 px-2 text-right font-mono ${t.type === 'expense' ? 'text-red-400' : 'text-green-400'}`}>
                                {t.type === 'expense' ? '-' : '+'}{formatCurrency(t.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {reportData.transactions.length > 50 && (
                        <p className="text-center text-muted-foreground mt-4 text-sm">
                          Showing 50 of {reportData.transactions.length}. Download report for full list.
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No transactions found</p>
                  )}
                </div>
              </div>
            )}

            {!reportData && (
              <div className="p-12 bg-white/5 border border-white/10 rounded-2xl text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-semibold mb-2">Select Filters</h3>
                <p className="text-muted-foreground">Choose parameters and click "Generate Report"</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;
